import React, { useContext, useState } from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Spinner } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { PencilFill } from 'react-bootstrap-icons';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import QuoteDetailModal from '../Quote/QuoteDetailModal';
import OrderDetailModal from '../Order/OrderDetailModal';
import DeleteButton from '../Generic/DeleteButton';
import {
  getSupplierCreditDetails,
  deleteSupplierCredit,
} from '../../clients/supplier_credit_client';
import SupplierCreditCreateModal from './SupplierCreditCreateModal';
import { formatDateStr } from '../../config_and_helpers/helpers';

/**
 * Modal to display detailed information about a supplier credit,
 * including the originating quote and all orders that used this credit.
 *
 * @param {Object} creditObj - Optional credit summary object from list.
 * @param {number} creditId - Credit id (used when creditObj is not provided).
 * @param {function} onCreditUpdated - Optional callback invoked after a successful edit so parents can refresh.
 */
const SupplierCreditDetailModal = ({ creditObj, creditId, onCreditUpdated }) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [show, setShow] = useState(false);
  const [credit, setCredit] = useState(creditObj);
  const creditIdToUse = creditObj ? creditObj.id : creditId;

  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleClose = () => setShow(false);

  const fetchDetailedData = async (force = false) => {
    if (!creditIdToUse || loadingDetails) return;
    if (!force && detailedData) return;
    setLoadingDetails(true);
    setError(null);
    try {
      const data = await getSupplierCreditDetails(token, creditIdToUse);
      setDetailedData(data);
      if (!credit) {
        setCredit(data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load supplier credit details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setDetailedData(null);
    fetchDetailedData(true);
    onCreditUpdated?.();
  };

  const handleShow = () => {
    setShow(true);
    fetchDetailedData();
  };

  if (!creditIdToUse) {
    return null;
  }

  const supplierObj =
    credit ||
    (creditObj && {
      id: creditObj.supplier_id,
      name: creditObj.supplier_name,
    });
  const hasRelatedOrders =
    detailedData?.order != null ||
    detailedData?.originating_order != null ||
    (Array.isArray(detailedData?.orders_using_credit) &&
      detailedData.orders_using_credit.length > 0);

  return (
    <div>
      <Button
        variant="link"
        onClick={handleShow}
        style={{
          padding: 0,
          border: 'none',
          background: 'none',
          color: '#0d6efd',
          textDecoration: 'underline',
        }}
      >
        {credit?.name || `Credit #${creditIdToUse}`}
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="supplier-credit-detail-modal"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Supplier Credit {creditIdToUse}
            {supplierObj?.name ? ` – ${supplierObj.name}` : ''}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingDetails && (
            <div className="text-center mb-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2">Loading supplier credit details...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger mb-3">
              <p className="mb-2">
                Error loading supplier credit details: {error}
              </p>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={fetchDetailedData}
              >
                Retry
              </Button>
            </div>
          )}

          {!loadingDetails && !error && !detailedData && (
            <div className="alert alert-info mb-3">
              <p className="mb-0">Loading supplier credit details...</p>
            </div>
          )}

          {detailedData && (
            <Container>
              <Row className="mb-3">
                <Col>
                  <p className="fs-6 fw-bold">Supplier:</p>
                </Col>
                <Col>
                  {supplierObj ? (
                    <SupplierDetailModal
                      supplierObj={supplierObj}
                      supplierId={supplierObj.id}
                      smallerFont
                    />
                  ) : (
                    <span className="text-muted">Unknown</span>
                  )}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <p className="fs-6 fw-bold">Initial amount:</p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {detailedData.initial_amount} {detailedData.currency}
                  </p>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <p className="fs-6 fw-bold">Remaining amount:</p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {detailedData.remaining_amount} {detailedData.currency}
                  </p>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <p className="fs-6 fw-bold">Created at:</p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {detailedData.created_at
                      ? formatDateStr(detailedData.created_at)
                      : ''}
                  </p>
                </Col>
              </Row>
              <Row className="mb-4">
                <Col>
                  <p className="fs-6 fw-bold">Updated at:</p>
                </Col>
                <Col>
                  <p className="fs-6">
                    {detailedData.updated_at
                      ? formatDateStr(detailedData.updated_at)
                      : ''}
                  </p>
                </Col>
              </Row>

              {/* Originating order (if provided) */}
              {(detailedData.originating_order || detailedData.order) && (
                <>
                  <h5 className="mt-3">Originating Order</h5>
                  <Table striped bordered hover size="sm" className="mt-2">
                    <thead>
                      <tr className="text-center">
                        <th>Order</th>
                        <th>Order Ref</th>
                        <th>Project</th>
                        <th>Created at</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="text-center align-middle">
                        <td>
                          <OrderDetailModal
                            orderId={
                              detailedData.originating_order?.id ??
                              detailedData.order
                            }
                          />
                        </td>
                        <td>
                          {detailedData.originating_order?.corporate_order_ref ??
                            '—'}
                        </td>
                        <td>
                          {detailedData.originating_order?.project ?? '—'}
                        </td>
                        <td>
                          {Array.isArray(
                            detailedData.originating_order?.reception_dates
                          ) &&
                          detailedData.originating_order.reception_dates
                            .length > 0
                            ? formatDateStr(
                                detailedData.originating_order
                                  .reception_dates[0]
                              )
                            : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </>
              )}

              {/* Originating quote (if provided) */}
              {detailedData.originating_quote && (
                <>
                  <h5 className="mt-3">Originating Quote</h5>
                  <Table striped bordered hover size="sm" className="mt-2">
                    <thead>
                      <tr className="text-center">
                        <th>ID</th>
                        <th>Demand Ref</th>
                        <th>Status</th>
                        <th>Budget</th>
                        <th>Prepayment</th>
                        <th>Project</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="text-center align-middle">
                        <td>
                          <QuoteDetailModal
                            quoteObj={detailedData.originating_quote}
                            quoteId={detailedData.originating_quote.id}
                          />
                        </td>
                        <td>
                          {detailedData.originating_quote.corporate_demand_ref ??
                            '—'}
                        </td>
                        <td>{detailedData.originating_quote.status}</td>
                        <td>{detailedData.originating_quote.budget ?? '—'}</td>
                        <td>
                          {detailedData.originating_quote.prepayment_amount
                            ? `${detailedData.originating_quote.prepayment_amount} ${detailedData.originating_quote.prepayment_currency || ''
                              }`
                            : '—'}
                        </td>
                        <td>
                          {detailedData.originating_quote.project ?? '—'}
                        </td>
                        <td>
                          {detailedData.originating_quote.created_at
                            ? formatDateStr(
                              detailedData.originating_quote.created_at
                            )
                            : ''}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </>
              )}

              {/* Orders using this credit */}
              {Array.isArray(detailedData.orders_using_credit) &&
                detailedData.orders_using_credit.length > 0 && (
                  <>
                    <h5 className="mt-4">Orders Using This Credit</h5>
                    <Table striped bordered hover size="sm" className="mt-2">
                      <thead>
                        <tr className="text-center">
                          <th>Order</th>
                          <th>Order Ref</th>
                          <th>Status</th>
                          <th>Reception dates</th>
                          <th>Charged from credit</th>
                          <th>Project</th>
                          <th>User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedData.orders_using_credit.map(order => (
                          <tr
                            key={order.id}
                            className="text-center align-middle"
                          >
                            <td>
                              <OrderDetailModal
                                order={order}
                                orderId={order.id}
                              />
                            </td>
                            <td>{order.corporate_order_ref ?? '—'}</td>
                            <td>{order.status}</td>
                            <td>
                              {Array.isArray(order.reception_dates) &&
                                order.reception_dates.length > 0
                                ? order.reception_dates
                                  .map(d => formatDateStr(d))
                                  .join(', ')
                                : '—'}
                            </td>
                            <td>
                              {order.amount_charged != null
                                ? order.amount_charged
                                : '—'}
                            </td>
                            <td>{order.project ?? '—'}</td>
                            <td>{order.user ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
            </Container>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex flex-row justify-content-between">
          {detailedData && !isViewer ? (
            <div className="d-flex flex-row">
              <div className="me-2">
                <Button
                  variant="outline-success"
                  onClick={() => setShowEditModal(true)}
                >
                  <PencilFill />
                </Button>
              </div>
              <DeleteButton
                objectType="supplier credit"
                objectName={detailedData?.name || `Credit #${creditIdToUse}`}
                objectId={creditIdToUse}
                deleteFetchFunc={deleteSupplierCredit}
                onSuccessfulDelete={() => {
                  handleClose();
                  onCreditUpdated?.();
                }}
                disableDelete={hasRelatedOrders}
                disabledTooltipReason="Cannot delete: credit is linked to one or more orders."
              />
            </div>
          ) : (
            <div />
          )}
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {showEditModal && detailedData && (
        <SupplierCreditCreateModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
          creditObj={detailedData}
        />
      )}
    </div>
  );
};

export default SupplierCreditDetailModal;

